import React, { useState, useEffect } from 'react';
import { useAdminContext } from '../../context/AdminContext.js';
import Swal from 'sweetalert2'; 
import '../../styles/GestionDePagos.css';
import { ExternalLink, Check, X } from 'lucide-react';
import Pagination from '../../components/common/Pagination.js'; // Importar el componente de paginación
import ImagePreviewModal from '../../components/common/ImagePreviewModal.js';

// Función helper para obtener el enlace del explorador de blockchain
const getExplorerLink = (network, txId) => {
    if (!network || !txId) return null;
    const upperNetwork = network.toUpperCase();
    if (upperNetwork.includes('TRC20')) return `https://tronscan.org/#/transaction/${txId}`;
    if (upperNetwork.includes('BEP20')) return `https://bscscan.com/tx/${txId}`;
    if (upperNetwork.includes('ERC20')) return `https://etherscan.io/tx/${txId}`;
    return null;
};

// Componente para la tarjeta de solicitud compacta
const SolicitudCard = ({ solicitud, onAction, onCancel, onDeactivateAccelerator, processingId, setPreview }) => {
    const [isTest, setIsTest] = useState(false); // Estado para el checkbox
    const isProcessing = processingId === solicitud.id;
    const explorerLink = getExplorerLink(solicitud.depositNetwork, solicitud.transactionId);
    const date = solicitud.createdAt?.toDate().toLocaleString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });

    // --- LÓGICA DE ACCIONES REFACTORIZADA ---
    const renderActions = () => {
        switch (solicitud.status) {
            case 'cancelado':
            case 'rechazado':
                const reason = solicitud.status === 'cancelado' ? solicitud.cancellationReason : solicitud.rejectionReason;
                return <p className="solicitud-card-compact-reason">{reason || 'No se especificó un motivo.'}</p>;
            default:
                // El caso 'pendiente' se convierte en el predeterminado para limpiar el switch
                return (
                    <div className="solicitud-card-compact-actions">
                        <div className="actions-left">
                            <div className="test-checkbox">
                                <input type="checkbox" id={`isTest-${solicitud.id}`} checked={isTest} onChange={(e) => setIsTest(e.target.checked)} />
                                <label htmlFor={`isTest-${solicitud.id}`}>Promoción</label>
                            </div>
                        </div>
                        <div className="actions-right">
                            <button onClick={() => onAction(solicitud, 'aprobado', isTest)} disabled={isProcessing} className="approve-btn">
                                <Check size={16} /> {isProcessing ? '...' : 'Aprobar'}
                            </button>
                            <button onClick={() => onAction(solicitud, 'rechazado')} disabled={isProcessing} className="reject-btn">
                                <X size={16} /> {isProcessing ? '...' : 'Rechazar'}
                            </button>
                        </div>
                    </div>
                );
        }
    };

    // --- MEJORA: Añadir clase específica para acuerdos de prueba ---
    const cardClassName = `solicitud-card-compact status-${solicitud.status} ${solicitud.status === 'aprobado' && solicitud.isTest ? 'is-test' : ''}`;

    return (
        <div className={cardClassName}>
            <div className="solicitud-card-compact-header">
                <span className="user-email" title={solicitud.userEmail}>{solicitud.userEmail}</span>
                <span className="date">{date}</span>
            </div>
            <div className="solicitud-card-compact-body">
                <div className="info-row">
                    <span className="label">Monto:</span>
                    <span className="value amount">${(solicitud.amount || 0).toFixed(2)}</span>
                </div>
                <div className="info-row">
                    <span className="label">Red:</span>
                    {(solicitud.proofUrl || solicitud.paymentProofUrl) && (
                        <button type="button" onClick={() => setPreview(solicitud.proofUrl || solicitud.paymentProofUrl)} className="link-like" style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}>Ver Comprobante</button>
                    )}
                </div>
                <div className="info-row links">
                    {explorerLink && <a href={explorerLink} target="_blank" rel="noopener noreferrer" title="Ver en explorador de bloques"><ExternalLink size={14} /> Ver TxID</a>}
                    {(solicitud.proofUrl || solicitud.paymentProofUrl) && (
                        // Mostrar una miniatura clicable en lugar de un enlace crudo
                        <button type="button" onClick={() => setPreview(solicitud.proofUrl || solicitud.paymentProofUrl)} style={{ background: 'transparent', border: 'none', padding: 0, marginLeft: 8, cursor: 'pointer' }} title="Ver comprobante">
                            <img src={solicitud.proofUrl || solicitud.paymentProofUrl} alt="comprobante-thumb" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6, boxShadow: '0 1px 4px rgba(0,0,0,0.15)' }} />
                        </button>
                    )}
                </div>
            </div>
            {/* Mostramos las acciones solo si el estado es 'pendiente' */}
            {solicitud.status === 'pendiente' && renderActions()}
            {/* Mostramos el motivo si está 'rechazado' o 'cancelado' */}
            {(solicitud.status === 'rechazado' || solicitud.status === 'cancelado') && (
                <p className="solicitud-card-compact-reason">{solicitud.status === 'rechazado' ? solicitud.rejectionReason : solicitud.cancellationReason || 'No se especificó un motivo.'}</p>
            )}
        </div>
    );
};

// Nuevo sub-componente para manejar la lógica de las solicitudes de depósito
const SolicitudesDeDeposito = () => {
    const {
        allAgreements, // CORRECCIÓN: Usar la lista completa de acuerdos
        loading,
        processingId,
        handleStatusUpdate,
        handleCancelAgreement,
        handleDeactivateAccelerator
    } = useAdminContext() || { allAgreements: [], loading: true };

    // Estado para manejar la URL de previsualización global dentro de este componente
    const [previewUrl, setPreviewUrl] = useState(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [activeFilterTab, setActiveFilterTab] = useState('pendiente');
    const [searchTerm, setSearchTerm] = useState('');
    const [searchAmount, setSearchAmount] = useState('');

    useEffect(() => {
        setCurrentPage(1); // Reset page on filter change
    }, [activeFilterTab, searchTerm, searchAmount]);

    const filteredAgreements = (allAgreements || []).filter(a => {
        const statusFilter = 
            (activeFilterTab === 'pendiente' && a.status === 'pendiente') ||
            (activeFilterTab === 'aprobado_real' && a.status === 'aprobado' && !a.isTest) ||
            (activeFilterTab === 'aprobado_prueba' && a.status === 'aprobado' && a.isTest) ||
            (activeFilterTab === 'rechazado' && a.status === 'rechazado') ||
            (activeFilterTab === 'cancelado' && a.status === 'cancelado') ||
            (activeFilterTab === 'completado' && (a.status === 'completado' || a.status === 'completed'));

        const searchFilter = searchTerm.trim() === '' || 
                             (a.userEmail && a.userEmail.toLowerCase().includes(searchTerm.toLowerCase()));

        const amountFilter = searchAmount.trim() === '' || 
                             (a.amount && a.amount.toString() === searchAmount.trim());

        return statusFilter && searchFilter && amountFilter;
    });

    const totalPages = Math.ceil(filteredAgreements.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedAgreements = filteredAgreements.slice(startIndex, startIndex + itemsPerPage);

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const promptAndReject = (solicitud) => {
        Swal.fire({
            title: 'Rechazar Solicitud',
            text: `Estás a punto de rechazar el aporte de ${solicitud.userEmail}.`,
            input: 'text',
            inputLabel: 'Motivo del rechazo',
            inputPlaceholder: 'Escribe un motivo claro para el usuario...',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            confirmButtonText: 'Sí, rechazar',
            cancelButtonText: 'Cancelar',
            inputValidator: (value) => {
                if (!value || value.trim().length < 10) {
                    return 'Debes introducir un motivo de al menos 10 caracteres.';
                }
            }
        }).then(result => result.isConfirmed && handleStatusUpdate(solicitud, 'rechazado', { rejectionReason: result.value }));
    };

    const confirmAndCancel = (agreement) => {
        Swal.fire({
            title: '¿Estás seguro?',
            text: `Vas a iniciar el proceso para cancelar el acuerdo #${agreement.id.substring(0, 6)}. Se te pedirá un motivo.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            confirmButtonText: 'Sí, continuar',
            cancelButtonText: 'No, volver'
        }).then((result) => {
            if (result.isConfirmed) {
                handleCancelAgreement(agreement);
            }
        });
    };

    return (
        <>
            <div className="filters-and-tabs-container">
                <div className="admin-tabs">
                    <button onClick={() => setActiveFilterTab('pendiente')} className={activeFilterTab === 'pendiente' ? 'active' : ''}>Pendientes</button>
                    <button onClick={() => setActiveFilterTab('aprobado_real')} className={activeFilterTab === 'aprobado_real' ? 'active' : ''}>Aprobados Reales</button>
                    <button onClick={() => setActiveFilterTab('aprobado_prueba')} className={activeFilterTab === 'aprobado_prueba' ? 'active' : ''}>Aprobados de Promoción</button>
                    <button onClick={() => setActiveFilterTab('rechazado')} className={activeFilterTab === 'rechazado' ? 'active' : ''}>Rechazados</button>
                    <button onClick={() => setActiveFilterTab('cancelado')} className={activeFilterTab === 'cancelado' ? 'active' : ''}>Cancelados</button>
                    <button onClick={() => setActiveFilterTab('completado')} className={activeFilterTab === 'completado' ? 'active completados' : 'completados'}>Completados</button>
                </div>
                <div className="search-input-container">
                    <input
                        type="text"
                        placeholder="Buscar por email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <input
                        type="number"
                        placeholder="Buscar por monto exacto..."
                        value={searchAmount}
                        onChange={(e) => setSearchAmount(e.target.value)}
                        min="0"
                    />
                </div>
            </div>
            {loading ? (
                <p className="loading-text">Cargando solicitudes...</p>
            ) : paginatedAgreements.length > 0 ? (
                <>
                    <div className="solicitudes-grid-compact">
                        {paginatedAgreements.map(agreement => (
                            <SolicitudCard 
                                key={agreement.id} 
                                solicitud={agreement} 
                                setPreview={setPreviewUrl}
                                onAction={(sol, status, isTest) => {
                                    if (status === 'rechazado') {
                                        promptAndReject(sol);
                                    } else {
                                        handleStatusUpdate(sol, status, { isTest: isTest });
                                    }
                                }}
                                onCancel={confirmAndCancel}
                                onDeactivateAccelerator={handleDeactivateAccelerator}
                                processingId={processingId}
                            />
                        ))}
                    </div>
                    {totalPages > 1 && (
                        <Pagination
                            totalItems={filteredAgreements.length}
                            itemsPerPage={itemsPerPage}
                            onItemsPerPageChange={setItemsPerPage}
                            currentPage={currentPage}
                            onNext={handleNextPage}
                            onPrevious={handlePreviousPage}
                            canNext={currentPage < totalPages}
                            canPrevious={currentPage > 1}
                        />
                    )}
                </>
            ) : (
                <p className="empty-state-text">No hay solicitudes en la categoría seleccionada.</p>
            )}
            {/* Modal de previsualización de comprobante */}
            <ImagePreviewModal url={previewUrl} onClose={() => setPreviewUrl(null)} />
        </>
    );
};

// Componente principal que gestiona las pestañas de alto nivel
const GestionDePagos = () => {
    return (
        <div className="main-panel">
            {/* El título ahora es más específico para esta sección. */}
            <h2 className="section-title">Solicitudes de Aporte</h2>
            <p>Revisa y gestiona las solicitudes de aporte de los usuarios.</p>
            
            {/* Se elimina el contenedor de pestañas y se renderiza directamente el componente de solicitudes. */}
            <div className="tab-content" style={{ marginTop: '1.5rem' }}>
                <SolicitudesDeDeposito />
            </div>
        </div>
    );
};

export default GestionDePagos;